import { getSbefCode, getSbefLifetimeById } from '../../sbef.helpers';
import { getNodeValue } from '../utils/get-node-value.helper';
import { BUILDING_LIFETIME_DEFAULT } from '../../project-factory.helpers';
import { required } from '../../function.helpers';
import { guards } from '../../typeguards.helpers';
import { EXPRESSION_VARIABLE_NAME } from '@shared/constants/expression-variables-names.constant';
import { ElementPropertyName } from '@shared/models/element-property.model';

/**
 * Math.js extension name for {@link getSBEFLifetime}. Single source of truth so the
 * compute pipeline can detect expressions that read ancestor lifetime via
 * `user_defined.lifetime` / `scope.get('lifetime')` (implicit reads invisible to
 * static parsing).
 */
export const GET_SBEF_LIFETIME_FN_NAME = 'getSBEFLifetime';

export function getSBEFLifetime(args: any[], math: any, scope: any): number {
  const sbefPropertyValue = getNodeValue(args[0], math, scope);
  const code = getSbefCode(sbefPropertyValue);
  const sbefLifetime = code ? getSbefLifetimeById(code) : undefined;

  // Explicit user-set lifetime on self/ancestor (cascaded via user_defined); ignore ≤ 0
  const userDefinedLifetime = scope.get(
    EXPRESSION_VARIABLE_NAME.USER_DEFINED,
  )?.[ElementPropertyName.Lifetime];
  const explicitLifetime =
    guards.is.number(userDefinedLifetime) && userDefinedLifetime > 0
      ? userDefinedLifetime
      : undefined;

  // Get first defined lifetime value in prio order (mirrors getProductElementLifetime for B4)
  return required(
    [
      explicitLifetime, // 1. Explicitly set by the user on the element or an ancestor (DEV-3549)
      scope.get(EXPRESSION_VARIABLE_NAME.PRODUCT_LIFETIME), // 2. Product service-life override
      sbefLifetime, // 3. From SBEF code table
      scope.get(ElementPropertyName.Lifetime), // 4. From parent element (incl. SBEF/fallback — not user-set)
      scope.get(EXPRESSION_VARIABLE_NAME.BUILDING_LIFETIME), // 5. User defined building lifetime
      BUILDING_LIFETIME_DEFAULT, // 6. Fallback to building lifetime default
    ].find((l) => guards.is.number(l)),
  );
}

getSBEFLifetime.rawArgs = true;
